import { IObjectTranslatable, RelationalActionTranslator, TextAlignKindsTranslator } from ".";
import { IMovableTextBoxItemBean, ITypeDefinable, BeanTypes } from "../beans/items";
import { IItem, MovableTextBoxItem } from "../../../../domain/document/items";
import { HtmlColorCodeTranslator } from "./HtmlColorCodeTranslator";
import { RelationalActionExtractor } from "./RelationalActionExtractor";
import {
  Point,
  Size,
  Rectangle,
  Color,
  TextStyle,
} from "../../../../domain/drawing";
import { MovableTextBoxInputMethodOptionKinds } from "../../../../domain/document/inputMethodOptionKinds";
import { MovableTextBoxInputMethodKinds } from "../../../../domain/document/inputMethodKinds";

export class MovableTextBoxItemTranslator implements IObjectTranslatable {
  toItem(item: ITypeDefinable, formatId: number, groupId: number, documentId: number, executionMode: string): IItem {
    const bean = item as IMovableTextBoxItemBean;
    const result = new MovableTextBoxItem(bean.text);
    result.privateId = (bean as any).privateId; // フロントエンド独自パラメータ.
    result.uniqueKey = bean.uniqueKey;
    result.id = bean.id;
    result.sectionObjectUid = bean.sectionObjectUid;
    result.isEditable = true; // TODO:特に問題なければプロパティを削除する
    result.isAdditional = !!bean.isAdditional;
    result.isDeleted = !!bean.isDeleted;
    result.isVisible = !!bean.isVisible;
    result.typeDetail = bean.typeDetail;
    result.viewTitle = bean.viewTitle;
    result.printBehavior.shouldPrint = !!bean.printVisible;
    result.printBehavior.shouldPreview = !!bean.previewVisible;
    result.name = bean.name;
    result.actions = RelationalActionTranslator.toItems(bean.relationalItems);
    result.shouldQuote = !!bean.isQuotable;
    result.supportedQuotable = !!bean.supportedQuotable;
    result.geometry.rectangle = new Rectangle(new Point(bean.x, bean.y), new Size(bean.width, bean.height));
    result.textStyle = new TextStyle(new Color(bean.textColor), TextAlignKindsTranslator.toKind(bean.textAlign), bean.textFontSize, bean.adjustedTextFontSize, bean.textFontName, bean.textLineHeight, true);
    result.imeMode = bean.defaultIME;
    result.isFreeInputable = bean.supportedFreeText;
    result.inputMethodOptionKind = MovableTextBoxInputMethodOptionKinds.toInputMethodOptionKinds(bean);
    result.inputMethodKind = this.toInputMethodKind(bean, result.inputMethodOptionKind);
    result.enableSingleEmptyCheck = bean.emptyCheck;
    result.enableEmptyCheckConfirmation = bean.isEmptyCheckIgnorable;
    result.enableNameGroupEmptyCheck = bean.nameGroupEmptyCheck;
    result.alertGroupKey = bean.alertGroup;
    result.enabledAutoAdjustFontSize = !!bean.enabledAutoAdjustFontSize;
    result.toolTip = bean.toolTip;
    result.dataValidationKind = bean.dataValidation;
    result.autoNotePageSize = bean.autoNotePageSize;
    result.autoNotePageKind = bean.autoNotePageType;
    result.scriptExecuteParameters = RelationalActionExtractor.extractScriptExecuteParameters(result.actions);
    result.designatedDoctorNumberType = RelationalActionExtractor.extractDesignatedDoctorNumberType(result.actions);
    result.asteriskKind = bean.asteriskType;
    result.enableLengthCheck = !!bean.lengthCheck;
    result.lengthChecklength = bean.length;
    result.pasteDestinations = RelationalActionExtractor.extractTextPasteParameters(result.actions);
    result.parentAlertInformations = RelationalActionExtractor.extractParentAlertParameters(result.actions);
    result.mksTemplateFormats = RelationalActionExtractor.extractMksTemplateFormats(result.actions);
    result.mksTemplateRegisteredId = bean.mksTemplateRegisteredId;
    result.dynamicTemplateFormats = RelationalActionExtractor.extractDynamicTemplateFormats(result.actions);
    result.dynamicTemplateRegisteredId = bean.dynamicTemplateRegisteredId;
    result.paintStyle = HtmlColorCodeTranslator.toPaintStyleFill(bean.backColor);
    result.paintStyleOnEditing = bean.isDisplayBackColorSpecified
      ? HtmlColorCodeTranslator.toPaintStyleFill(bean.displayBackColor)
      : null;
    result.dacsProcessType = bean.dacsProcessType;
    result.dacsItemName = bean.dacsItemName;
    result.dacsItemCode = bean.dacsItemCode;
    result.isNaturalViewEnable = bean.naturalView;
    result.naturalText = bean.naturalText;
    return result;
  }

  private toInputMethodKind(bean: IMovableTextBoxItemBean, optionKind: MovableTextBoxInputMethodOptionKinds): MovableTextBoxInputMethodKinds {
    // MKS・DYNAMICテンプレートの指定になっていても、古い実装で移行されたデータではenabledFreeTextがtrueになってしまっているので対策している
    if (bean.enabledFreeText) {
      if (optionKind === MovableTextBoxInputMethodOptionKinds.MKSTemplate && (bean.mksTemplateRegisteredId || !bean.text)) {
        // 救済必要なデータなのでMKSテンプレート入力にする
        return MovableTextBoxInputMethodKinds.MKSTemplate;
      }
      if (optionKind === MovableTextBoxInputMethodOptionKinds.DynamicTemplate && (bean.dynamicTemplateRegisteredId || !bean.text)) {
        // 救済必要なデータなのでDynamicテンプレート入力にする
        return MovableTextBoxInputMethodKinds.DynamicTemplate;
      }
      return MovableTextBoxInputMethodKinds.FreeInput;
    }
    return MovableTextBoxInputMethodOptionKinds.getDefualtInputMethodKind(optionKind);
  }

  toBean(item: IItem): ITypeDefinable {
    const target = item as MovableTextBoxItem;
    const bean: IMovableTextBoxItemBean = {
      type: BeanTypes.MovableText,
      uniqueKey: target.uniqueKey,
      id: target.id,
      sectionObjectUid: target.sectionObjectUid,
      isVisible: target.isVisible,
      printVisible: target.printBehavior.shouldPrint,
      previewVisible: target.printBehavior.shouldPreview,
      x: Math.floor(target.geometry.rectangle.x),
      y: Math.floor(target.geometry.rectangle.y),
      width: Math.floor(target.geometry.rectangle.width),
      height: Math.floor(target.geometry.rectangle.height),
      isAdditional: target.isAdditional,
      isDeleted: target.isDeleted,
      name: target.name,
      textColor: HtmlColorCodeTranslator.fromColor(target.textStyle.color),
      textFontName: target.textStyle.fontFamily,
      textFontSize: target.textStyle.baseFontSize,
      textLineHeight: target.textStyle.lineHeight,
      textAlign: TextAlignKindsTranslator.fromKind(target.textStyle.textAlign),
      backColor: HtmlColorCodeTranslator.fromPaintStyle(target.paintStyle),
      displayBackColor: HtmlColorCodeTranslator.fromPaintStyle(target.paintStyleOnEditing),
      defaultIME: target.imeMode,
      supportedFreeText: target.isFreeInputable,
      enabledFreeText: target.shouldInputFree,
      text: target.value,
      relationalItems: null,  // TODO:返却する必要ある？
      emptyCheck: target.enableSingleEmptyCheck,
      isEmptyCheckIgnorable: target.enableEmptyCheckConfirmation,
      nameGroupEmptyCheck: target.enableNameGroupEmptyCheck,
      alertGroup: target.alertGroupKey,
      enabledAutoAdjustFontSize: target.enabledAutoAdjustFontSize,
      toolTip: target.toolTip,
      dataValidation: target.dataValidationKind,
      autoNotePageSize: target.autoNotePageSize,
      autoNotePageType: target.autoNotePageKind,
      isQuotable: target.shouldQuote,
      supportedQuotable: target.supportedQuotable,
      asteriskType: target.asteriskKind,
      lengthCheck: target.enableLengthCheck,
      length: target.lengthChecklength,
      adjustedTextFontSize: target.textStyle.adjustedFontSize,
      mksTemplateRegisteredId: target.mksTemplateRegisteredId,
      dynamicTemplateRegisteredId: target.dynamicTemplateRegisteredId,
      isDisplayBackColorSpecified: !!target.paintStyleOnEditing,
      typeDetail: target.typeDetail,
      viewTitle: target.viewTitle,
      dacsProcessType: target.dacsProcessType,
      dacsItemName: target.dacsItemName,
      dacsItemCode: target.dacsItemCode,
      naturalView: target.isNaturalViewEnable,
      naturalText: target.naturalText,
    };
    return bean;
  }
}
